import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { estagioHorarioService, estagioService } from '@/services/api';
import { PlusIcon, TrashIcon, CalendarIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Estagio, EstagioHorario } from '@/types';
import { useQueryInvalidation } from '@/hooks/useQueryInvalidation';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { ServerSearchSelect } from '@/components/ui/ServerSearchSelect';

// Schema para um único horário
const horarioItemSchema = z.object({
  horarioInicio: z.string().min(1, 'Horário de início é obrigatório'),
  horarioFim: z.string().min(1, 'Horário de fim é obrigatório'),
}).refine((data) => {
  if (data.horarioInicio && data.horarioFim) {
    const inicio = new Date(`2000-01-01T${data.horarioInicio}`);
    const fim = new Date(`2000-01-01T${data.horarioFim}`);
    return fim > inicio;
  }
  return true;
}, {
  message: 'Horário de fim deve ser maior que horário de início',
  path: ['horarioFim'],
});

// Schema principal do formulário
const horarioSchema = z.object({
  estagioId: z.number().min(1, 'ID do estágio é obrigatório'),
  diasSemana: z.array(z.enum(['Domingo', 'Segunda', 'Terca', 'Quarta', 'Quinta', 'Sexta', 'Sabado'])).min(1, 'Selecione pelo menos um dia da semana'),
  horarios: z.array(horarioItemSchema).min(1, 'Adicione pelo menos um horário'),
});

type HorarioItem = {
  horarioInicio: string;
  horarioFim: string;
};

type HorarioFormData = {
  estagioId: number;
  diasSemana: string[];
  horarios: HorarioItem[];
};

export const NovoHorarioPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEditing = !!id;
  const { invalidateHorarios } = useQueryInvalidation();
  const { handleError } = useErrorHandler();
  const [loadError, setLoadError] = useState<string | null>(null);

  // Obter estagioId da query string
  const estagioIdFromQuery = searchParams.get('estagioId');

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<HorarioFormData>({
    resolver: zodResolver(horarioSchema),
    defaultValues: {
      estagioId: 0,
      diasSemana: [],
      horarios: [{ horarioInicio: '', horarioFim: '' }],
    },
  });

  const watchedHorarios = watch('horarios');
  const watchedDiasSemana = watch('diasSemana');

  // Pré-selecionar estágio se estagioId estiver na query string
  useEffect(() => {
    if (estagioIdFromQuery) {
      const estagioId = Number(estagioIdFromQuery);
      if (Number.isInteger(estagioId) && estagioId > 0) {
        setValue('estagioId', estagioId);
      }
    }
  }, [estagioIdFromQuery, setValue]);

  // Buscar horário existente se estiver editando
  const { data: horarioData, isLoading: horarioLoading, error: horarioError, refetch: refetchHorario } = useQuery<EstagioHorario>({
    queryKey: ['horario', id],
    queryFn: () => estagioHorarioService.getById(parseInt(id!)),
    enabled: !!id,
  });

  useEffect(() => {
    if (horarioError) {
      const message = handleError(horarioError as any, 'Erro ao carregar horário');
      setLoadError(message);
    }
  }, [horarioError, handleError]);

  useEffect(() => {
    if (!horarioError && (!isEditing || horarioData)) {
      setLoadError(null);
    }
  }, [horarioError, horarioData, isEditing]);

  // Preencher formulário com dados existentes (modo edição simples para compatibilidade)
  useEffect(() => {
    if (horarioData) {
      setValue('estagioId', horarioData.estagioId);
      setValue('diasSemana', [horarioData.diaSemana]);
      setValue('horarios', [{
        horarioInicio: horarioData.horarioInicio,
        horarioFim: horarioData.horarioFim
      }]);

    }
  }, [horarioData, setValue]);

  // Funções para gerenciar horários
  const addHorario = () => {
    const currentHorarios = watchedHorarios || [];
    setValue('horarios', [...currentHorarios, { horarioInicio: '', horarioFim: '' }]);
  };

  const removeHorario = (index: number) => {
    const currentHorarios = watchedHorarios || [];
    if (currentHorarios.length > 1) {
      const newHorarios = currentHorarios.filter((_, i) => i !== index);
      setValue('horarios', newHorarios);
    }
  };

  const updateHorario = (index: number, field: keyof HorarioItem, value: string) => {
    const currentHorarios = watchedHorarios || [];
    const newHorarios = [...currentHorarios];
    newHorarios[index] = { ...newHorarios[index], [field]: value };
    setValue('horarios', newHorarios);
  };

  // Funções para gerenciar dias da semana
  const toggleDiaSemana = (dia: string) => {
    const currentDias = watchedDiasSemana || [];
    const newDias = currentDias.includes(dia)
      ? currentDias.filter(d => d !== dia)
      : [...currentDias, dia];
    setValue('diasSemana', newDias);
  };

  const selectSegundaSexta = () => {
    const diasUteis = ['Segunda', 'Terca', 'Quarta', 'Quinta', 'Sexta'];
    setValue('diasSemana', diasUteis);
  };

  const selectTodosDias = () => {
    const todosDias = diasSemana.map(dia => dia.value);
    setValue('diasSemana', todosDias);
  };

  const limparDias = () => {
    setValue('diasSemana', []);
  };

  const createHorarioMutation = useMutation({
    mutationFn: async (data: HorarioFormData) => {
      // Usar a API de lote para criar múltiplos horários de uma vez
      return estagioHorarioService.createLote({
        estagioId: data.estagioId,
        diasSemana: data.diasSemana,
        horarios: data.horarios,
      });
    },
    onSuccess: (results, variables) => {
      invalidateHorarios({ estagioId: variables.estagioId });
      toast.success(`${results.length} horário(s) criado(s) com sucesso!`);
      navigate('/horarios');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao criar horários');
    },
  });

  const updateHorarioMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: HorarioFormData }) => {
      // Para edição, manter compatibilidade com horário único
      const firstDia = data.diasSemana[0];
      const firstHorario = data.horarios[0];

      return estagioHorarioService.update(id, {
        estagioId: data.estagioId,
        diaSemana: firstDia as any,
        horarioInicio: firstHorario.horarioInicio,
        horarioFim: firstHorario.horarioFim,
      });
    },
    onSuccess: (_result, variables) => {
      invalidateHorarios({ estagioId: variables.data.estagioId, horarioId: variables.id });
      toast.success('Horário atualizado com sucesso!');
      navigate('/horarios');
    },
    onError: (error: any) => {
      handleError(error, 'Erro ao atualizar horário');
    },
  });

  const onSubmit = async (data: HorarioFormData) => {
    try {
      setIsSubmitting(true);
      if (isEditing) {
        await updateHorarioMutation.mutateAsync({ id: parseInt(id!), data });
      } else {
        await createHorarioMutation.mutateAsync(data);
      }
    } catch (error) {
      // Error is handled by mutation
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{isEditing ? 'Editar Horário' : 'Novo Horário'}</h1>
            <p className="mt-1 text-sm text-gray-500">{loadError}</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                if (isEditing) {
                  refetchHorario();
                }
              }}
            >
              Tentar novamente
            </Button>
            <Button variant="outline" onClick={() => navigate('/horarios')}>
              Cancelar
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isEditing && horarioLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-2 text-gray-500">
            Carregando dados do horário...
          </p>
        </div>
      </div>
    );
  }
  // Dias da semana para o /horarios/novo
  const diasSemana = [
    { value: 'Domingo', label: 'Domingo' },
    { value: 'Segunda', label: 'Segunda' },
    { value: 'Terca', label: 'Terça' },
    { value: 'Quarta', label: 'Quarta' },
    { value: 'Quinta', label: 'Quinta' },
    { value: 'Sexta', label: 'Sexta' },
    { value: 'Sabado', label: 'Sábado' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
          {isEditing ? 'Editar Horário' : 'Novo Horário'}
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          {isEditing ? 'Edite as informações do horário de estágio' : 'Cadastre um novo horário de estágio no sistema'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Seleção do Estágio */}
        <Card>
          <CardHeader>
            <CardTitle>Estágio</CardTitle>
            <CardDescription>
              Selecione o estágio para cadastrar os horários
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div>
              <Label htmlFor="estagioId">Estágio *</Label>
              <ServerSearchSelect<Estagio>
                id="estagioId"
                value={watch('estagioId') || null}
                onChange={(selectedId) => setValue('estagioId', selectedId ?? 0, { shouldValidate: true })}
                fetchPage={(params) => estagioService.list(params)}
                fetchById={(selectedId) => estagioService.getById(selectedId)}
                getOptionLabel={(estagio: Estagio) => `Estágio #${estagio.id} — ${estagio.aluno?.nome ?? 'Aluno não informado'}`}
                queryKey="estagios-horarios-select"
                placeholder="Buscar estágio por aluno ou empresa…"
                error={Boolean(errors.estagioId)}
              />
              {errors.estagioId && (
                <p className="mt-1 text-sm text-error-600">{errors.estagioId.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Seleção de Dias da Semana */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <CalendarIcon className="h-5 w-5" />
              <span>Dias da Semana</span>
            </CardTitle>
            <CardDescription>
              Selecione os dias da semana que terão os mesmos horários
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Botões de seleção rápida */}
            <div className="flex flex-wrap gap-2 mb-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={selectSegundaSexta}
                className="text-xs"
              >
                Segunda a Sexta
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={selectTodosDias}
                className="text-xs"
              >
                Todos os Dias
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={limparDias}
                className="text-xs text-red-600 hover:text-red-700"
              >
                Limpar
              </Button>
            </div>

            {/* Grid dos dias da semana */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
              {diasSemana.map((dia) => (
                <label
                  key={dia.value}
                  className={`flex flex-col items-center p-3 border-2 rounded-lg cursor-pointer transition-colors ${watchedDiasSemana?.includes(dia.value)
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-gray-200 hover:border-gray-300'
                    }`}
                >
                  <input
                    type="checkbox"
                    checked={watchedDiasSemana?.includes(dia.value) || false}
                    onChange={() => toggleDiaSemana(dia.value)}
                    className="sr-only"
                  />
                  <span className="text-sm font-medium">{dia.label}</span>
                </label>
              ))}
            </div>
            {errors.diasSemana && (
              <p className="mt-2 text-sm text-error-600">{errors.diasSemana.message}</p>
            )}
          </CardContent>
        </Card>

        {/* Horários */}
        <Card>
          <CardHeader>
            <CardTitle>Horários</CardTitle>
            <CardDescription>
              Configure os horários que se repetirão nos dias selecionados
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {watchedHorarios?.map((horario, index) => (
              <div key={index} className="flex flex-col sm:flex-row sm:items-end gap-3 p-4 border border-gray-200 rounded-lg">
                <div className="flex-1">
                  <Label>Horário de Início</Label>
                  <Input
                    type="time"
                    value={horario.horarioInicio}
                    onChange={(e) => updateHorario(index, 'horarioInicio', e.target.value)}
                    className="mt-1"
                  />
                  {errors.horarios?.[index]?.horarioInicio && (
                    <p className="mt-1 text-sm text-error-600">
                      {errors.horarios[index]?.horarioInicio?.message}
                    </p>
                  )}
                </div>

                <div className="flex-1">
                  <Label>Horário de Fim</Label>
                  <Input
                    type="time"
                    value={horario.horarioFim}
                    onChange={(e) => updateHorario(index, 'horarioFim', e.target.value)}
                    className="mt-1"
                  />
                  {errors.horarios?.[index]?.horarioFim && (
                    <p className="mt-1 text-sm text-error-600">
                      {errors.horarios[index]?.horarioFim?.message}
                    </p>
                  )}
                </div>

                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => removeHorario(index)}
                  disabled={watchedHorarios.length === 1}
                  className="flex items-center justify-center gap-1 w-full sm:w-auto"
                >
                  <TrashIcon className="h-4 w-4" />
                  <span>Remover</span>
                </Button>
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              onClick={addHorario}
              className="flex items-center space-x-2"
            >
              <PlusIcon className="h-4 w-4" />
              <span>Adicionar Horário</span>
            </Button>
          </CardContent>
        </Card>

        {/* Preview dos Horários */}
        {watchedDiasSemana && watchedDiasSemana.length > 0 && watchedHorarios && watchedHorarios.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Resumo dos Horários</CardTitle>
              <CardDescription>
                Preview dos horários que serão criados
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {watchedDiasSemana.map((dia) => (
                  <div key={dia} className="p-3 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">
                      {diasSemana.find(d => d.value === dia)?.label}
                    </h4>
                    <div className="space-y-1">
                      {watchedHorarios.map((horario, index) => (
                        <div key={index} className="flex items-center space-x-2 text-sm text-gray-600">
                          <span className="font-mono bg-white px-2 py-1 rounded border">
                            {horario.horarioInicio || '00:00'}
                          </span>
                          <span>às</span>
                          <span className="font-mono bg-white px-2 py-1 rounded border">
                            {horario.horarioFim || '00:00'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Total:</strong> {watchedDiasSemana.length * watchedHorarios.length} horário(s) serão criado(s)
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-6 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/horarios')}
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full sm:w-auto min-w-[120px]"
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{isEditing ? 'Salvando...' : 'Criando...'}</span>
              </div>
            ) : (
              isEditing ? 'Salvar Alterações' : 'Criar Horário'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};